import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios'; // Add axios import
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    CircularProgress,
    Typography,
    Tooltip
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import scanApi from '../../services/api';

// Add helper function for auth header
const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    };
};

const ReportPreviewDialog = ({ open, onClose, reportId, reportData }) => {
    const [loading, setLoading] = useState(true);
    const [content, setContent] = useState('');
    const [reportStatus, setReportStatus] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const iframeRef = useRef(null);
    const pollingIntervalRef = useRef(null);

    useEffect(() => {
        if (open && reportId) {
            startPolling();
        }
        return () => stopPolling();
    }, [open, reportId]);

    const startPolling = () => {
        setLoading(true);
        // Poll immediatamente e poi ogni 2 secondi
        checkReportStatus();
        pollingIntervalRef.current = setInterval(checkReportStatus, 2000);
    };

    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    };

    const checkReportStatus = async () => {
        try {
            const status = await scanApi.checkReportStatus(reportId);
            setReportStatus(status);

            if (status.status === 'completed') {
                stopPolling();
                await loadReportContent();
            } else if (['failed', 'aborted'].includes(status.status)) {
                stopPolling();
                setLoading(false);
                // Mostra errore
            }
        } catch (error) {
            console.error('Error checking report status:', error);
            stopPolling();
            setLoading(false);
        }
    };

    const loadReportContent = async () => {
        try {
            const htmlContent = await scanApi.getReportPreview(reportId);
            const cleanedContent = replaceAcunetixTextAndStyles(htmlContent);
            setContent(cleanedContent);
        } catch (error) {
            console.error('Error loading report:', error);
        } finally {
            setLoading(false);
        }
    };

    const replaceAcunetixTextAndStyles = (html) => {
        // Sostituzioni per il testo
        const replacements = {
            'Acunetix': 'ExploitFinder Scanner',
            'ACUNETIX': 'EXPLOITFINDER SCANNER',
            'acunetix': 'exploitfinder'
        };

        let modifiedHtml = html;

        // Replace testo nel contenuto
        Object.entries(replacements).forEach(([search, replace]) => {
            modifiedHtml = modifiedHtml.replace(new RegExp(search, 'gi'), replace);
        });

        // Aggiungi stili personalizzati
        const customStyles = `
            <style>
                /* Logo e branding personalizzati */
                .brand-logo { max-width: 240px; }
                .text-gradient { fill: #1a73e8; }
                @media (prefers-color-scheme: dark) {
                    .text-gradient { fill: rgb(8, 7, 7); }
                }
            </style>
        `;

        // Inserisci stili personalizzati prima di </head>
        modifiedHtml = modifiedHtml.replace('</head>', `${customStyles}</head>`);

        return modifiedHtml;
    };

    const handlePrint = () => {
        if (iframeRef.current) {
            const iframe = iframeRef.current;
            const iframeWindow = iframe.contentWindow;
            const iframeDoc = iframeWindow.document;

            // Add print styles
            const style = iframeDoc.createElement('style');
            style.textContent = `
                @media print {
                    body { margin: 0; padding: 20mm; }
                    @page { size: A4; margin: 20mm; }
                }
            `;
            iframeDoc.head.appendChild(style);

            iframeWindow.print();
        }
    };

    const handleDownloadPdf = async () => {
        try {
            if (!iframeRef.current) {
                throw new Error('Preview not ready');
            }

            const iframe = iframeRef.current;
            const iframeWindow = iframe.contentWindow;
            const iframeDoc = iframeWindow.document;

            // Add print-specific styles
            const style = iframeDoc.createElement('style');
            style.textContent = `
                @media print {
                    @page {
                        size: A4;
                        margin: 20mm;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    .no-print, 
                    button, 
                    .MuiButton-root {
                        display: none !important;
                    }
                    /* Improve table readability */
                    table {
                        break-inside: auto !important;
                        page-break-inside: auto !important;
                    }
                    tr {
                        break-inside: avoid !important;
                        page-break-inside: avoid !important;
                    }
                    /* Ensure proper page breaks */
                    h1, h2, h3, h4 {
                        break-after: avoid;
                        page-break-after: avoid;
                    }
                    img, ul, ol {
                        break-inside: avoid;
                        page-break-inside: avoid;
                    }
                }
            `;
            iframeDoc.head.appendChild(style);

            // Configure print settings
            const printSettings = {
                margin: {
                    top: '20mm',
                    right: '20mm',
                    bottom: '20mm',
                    left: '20mm'
                },
                pageSize: 'A4',
                printBackground: true
            };

            // Store original title
            const originalTitle = iframeDoc.title;
            
            // Set a more appropriate filename in title
            iframeDoc.title = `ExploitFinder-Scanner-Report-${reportId}`;

            // Print with browser's print dialog
            iframeWindow.print();

            // Restore original title
            iframeDoc.title = originalTitle;

        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    // Add this helper function
    const replaceTextInIframe = (iframe) => {
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        
        // Define the replacement script
        const replacementScript = `
            function replaceTextInPage(searchText, replaceText) {
                const walker = document.createTreeWalker(
                    document.body,
                    NodeFilter.SHOW_TEXT,
                    {
                        acceptNode: (node) => {
                            if (node.nodeValue.toLowerCase().includes(searchText.toLowerCase())) {
                                return NodeFilter.FILTER_ACCEPT;
                            }
                            return NodeFilter.FILTER_REJECT;
                        },
                    },
                    false
                );

                let node;
                while ((node = walker.nextNode())) {
                    node.nodeValue = node.nodeValue.replace(
                        new RegExp(searchText, "gi"),
                        replaceText
                    );
                }
            }

            // Execute replacements
            replaceTextInPage("acunetix", "ExploitFinder Scanner");
            replaceTextInPage("ACUNETIX", "EXPLOITFINDER SCANNER");
        `;

        // Create and append script element
        const script = doc.createElement('script');
        script.textContent = replacementScript;
        doc.head.appendChild(script);
    };

    // Add print preparation function
    const preparePrintStyles = (doc) => {
        const style = doc.createElement('style');
        style.textContent = `
            @media print {
                body { margin: 0; padding: 20mm; }
                @page { size: A4; margin: 20mm; }
                .no-print { display: none !important; }
                // Add any other print-specific styles here
            }
        `;
        doc.head.appendChild(style);
    };

    // Update the iframe ref callback
    const handleIframeLoad = () => {
        if (iframeRef.current) {
            replaceTextInIframe(iframeRef.current);
        }
    };

    const handleFullscreenToggle = () => {
        setIsFullscreen(!isFullscreen);
    };

    return (
        <Dialog 
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isFullscreen}
            PaperProps={{
                sx: { 
                    height: '90vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Report Preview
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {content && (
                        <>
                            <Tooltip title="Print">
                                <IconButton onClick={handlePrint} title="Print" disabled={loading}>
                                    <PrintIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                                <IconButton onClick={handleFullscreenToggle}>
                                    {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                </IconButton>
                            </Tooltip>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </>
                    )}
                </Box>
            </DialogTitle>
            <DialogContent sx={{ flex: 1, overflow: 'hidden' }}>
                {loading ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        height: '100%' 
                    }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>
                            {reportStatus?.status === 'processing' 
                                ? 'Generating report...' 
                                : 'Loading report...'}
                        </Typography>
                    </Box>
                ) : content ? (
                    <iframe
                        ref={iframeRef}
                        srcDoc={content}
                        style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                        }}
                        title="Report Preview"
                        onLoad={handleIframeLoad}
                    />
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        height: '100%'
                    }}>
                        <Typography color="error">
                            Failed to load report
                        </Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ReportPreviewDialog;
